import AsyncSelect from "react-select/async";
import debounce from "lodash/debounce";
import React, { Ref, useEffect, useMemo, useState } from "react";
import { MenuPlacement, MenuPosition } from "react-select";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { ClearIndicator, NoOptionsMessage, selectStyles } from "../config";
import { OptionType } from "../react-select-types";
import { useSharedTranslations } from "../../../lib";
import styles from "../react-select.module.scss";

interface IAsyncSelectProps {
    ref?: Ref<any>;
    menuPlacement?: MenuPlacement;
    menuPosition?: MenuPosition;
    value?: OptionType | OptionType[] | null;
    loadOptions: (inputValue: string, callback: (options: OptionType[]) => void) => Promise<OptionType[]> | void;
    disabled?: boolean;
    placeholder?: string;
    isSearchable?: boolean;
    hideClearIcon?: boolean;
    showSelected?: boolean;
    isMulti?: boolean;
    name?: string;
    className?: string;
    formatOptionLabel?: any;
    validate?: boolean;
    triggerValidate?: number;
    onChange?: (value: OptionType | OptionType[] | null) => void;
}

export const AsyncSearchableSelect: React.FC<IAsyncSelectProps> = ({
                                                                       value,
                                                                       loadOptions,
                                                                       className,
                                                                       name,
                                                                       placeholder = "",
                                                                       formatOptionLabel,
                                                                       validate,
                                                                       triggerValidate,
                                                                       menuPosition,
                                                                       menuPlacement,
                                                                       disabled = false,
                                                                       isMulti = false,
                                                                       showSelected = true,
                                                                       isSearchable = true,
                                                                       hideClearIcon = true,
                                                                       onChange,
                                                                       ref
                                                                   }) => {
    const [selectValue, setSelectValue] = useState<OptionType | OptionType[] | null>();
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();
    const t = useSharedTranslations();
    const { i18n } = useTranslation();
    const [width, setWidth] = useState<number>(window.innerWidth);
    const isDesktop = useMemo(() => {
        return width > 450;
    }, [width]);

    const selectMenuPlacement = useMemo(() => {
        if (menuPlacement) {
            return menuPlacement;
        } else {
            return isDesktop ? "auto" : "bottom";
        }
    }, [isDesktop, menuPlacement]);

    const selectMenuPosition = useMemo(() => {
        if (menuPosition) {
            return menuPosition;
        } else {
            return isDesktop ? "fixed" : "absolute";
        }
    }, [isDesktop, menuPosition]);
    const onWindowResize = () => {
        setWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener("resize", onWindowResize);

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    useEffect(() => {
        if (triggerValidate && validate) {
            validateValue(selectValue);
        } else {
            setIsError(false);
            setValidationError(undefined);
        }
    }, [selectValue, triggerValidate, validate]);

    useEffect(() => {
        if (isError) {
            validateValue(selectValue);
        }
    }, [i18n.language]);

    useEffect(() => {
        if (value) {
            setSelectValue(value);
        } else {
            setSelectValue(null);
        }
    }, [value]);

    function handleChange(val: any) {
        onChange && onChange(val);
        showSelected && setSelectValue(val);
    }

    const validateValue = (val: OptionType | OptionType[] | undefined | null) => {
        let error = false;
        let message: string | undefined = undefined;
        if (!val || (Array.isArray(val) && val.length === 0)) {
            error = true;
            message = t("requiredFieldError");
        }
        setIsError(error);
        setValidationError(message);
    };

    const onKeyDown = (e: React.KeyboardEvent<any>) => {
        if (isMenuOpened) {
            e.stopPropagation();
        }
    };

    const components: any = {
        NoOptionsMessage
    };

    if (hideClearIcon) {
        components["ClearIndicator"] = ClearIndicator;
    }

    return <div className={className}>
        <AsyncSelect
            ref={ref}
            className={cn(styles.reactSelect, isError ? styles.reactSelectError : undefined, disabled ? styles.reactSelectDisabled : undefined)}
            components={components}
            formatOptionLabel={formatOptionLabel}
            loadOptions={debounce(loadOptions, 500)}
            placeholder={placeholder}
            styles={selectStyles}
            value={selectValue}
            isDisabled={disabled}
            isClearable={true}
            isSearchable={isSearchable}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            onKeyDown={onKeyDown}
            onMenuOpen={() => setIsMenuOpened(true)}
            onMenuClose={() => setIsMenuOpened(false)}
            loadingMessage={() => t("loading")}
            menuPlacement={selectMenuPlacement}
            menuPosition={selectMenuPosition}
            name={name}
            onChange={handleChange}
            maxMenuHeight={!isDesktop ? 110 : undefined}
        />
        {isError && validationError && <div className={styles.validationError}>
            {validationError}
        </div>}
    </div>;
};
